import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useRealtime, sfx } from "../lib/realtime";
import SpinWheel from "./SpinWheel";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { Play, Loader2, Settings2, AlertTriangle, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ACCENTS = {
  attack: { color: "#2563eb", btn: "bg-blue-600 hover:bg-blue-500" },
  defense: { color: "#ea580c", btn: "bg-orange-600 hover:bg-orange-500" },
  penalty: { color: "#dc2626", btn: "bg-red-600 hover:bg-red-500" },
  custom: { color: "#7c3aed", btn: "bg-violet-600 hover:bg-violet-500" },
};

export default function WheelView({ slug, accentType = "custom", compact = false }) {
  const [wheel, setWheel] = useState(null);
  const [items, setItems] = useState([]);
  const [command, setCommand] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  const nonceRef = useRef(0);
  const accent = ACCENTS[accentType] || ACCENTS.custom;

  const load = useCallback(async () => {
    const [w, s] = await Promise.all([api.getWheel(slug), api.getSettings()]);
    setWheel(w);
    setSettings(s);
    const active = w.items.filter((i) => i.enabled).map((i, idx) => ({
      ...i, color: i.color || accent.color,
    }));
    setItems(active);
  }, [slug, accent.color]);

  useEffect(() => { load(); }, [load]);

  const audio = settings?.audio || { enabled: true, volume: 0.6 };

  const handleEvent = useCallback((ev) => {
    if (ev.type === "WHEEL_SPIN" && ev.wheel === slug) {
      setResult(null);
      setItems(ev.items);
      setSpinning(true);
      if (audio.enabled && audio.start) sfx.start(audio.volume);
      nonceRef.current += 1;
      setCommand({ winnerIndex: ev.winnerIndex, nonce: nonceRef.current, duration: settings?.general?.animationDuration || 5 });
      window.__lastSpin = ev;
    }
    if (ev.type === "OVERLAY_RESET") { setResult(null); setSpinning(false); }
  }, [slug, audio, settings]);

  useRealtime(handleEvent);

  const onRest = () => {
    setSpinning(false);
    const ev = window.__lastSpin;
    if (ev && ev.wheel === slug) {
      setResult({ name: ev.result, description: ev.resultDescription });
      if (audio.enabled && audio.result) sfx.result(audio.volume);
    }
  };

  const doSpin = async () => {
    try {
      sfx.ensure();
      await api.spin(slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante lo spin");
    }
  };

  if (!wheel) {
    return <div className="flex items-center justify-center h-64 text-slate-400"><Loader2 className="animate-spin mr-2" /> Caricamento…</div>;
  }

  const empty = items.length === 0;
  const size = compact ? 340 : 460;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <SpinWheel
          items={items}
          size={size}
          command={command}
          spinning={spinning}
          onRest={onRest}
          onStart={() => setSpinning(true)}
          onTick={() => audio.enabled && audio.tick && sfx.tick(audio.volume)}
        />
      </div>

      {empty && (
        <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-2">
          <AlertTriangle className="w-4 h-4" /> Nessun elemento attivo. Aggiungi o abilita almeno un elemento.
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button
          data-testid={`spin-${slug}-button`}
          onClick={doSpin}
          disabled={spinning || empty}
          className={`h-14 px-10 text-lg font-display font-bold uppercase tracking-wide text-white ${accent.btn}`}
        >
          {spinning ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2" />}
          {spinning ? "Girando…" : "Gira la ruota"}
        </Button>
        <Button
          data-testid={`overlay-hide-${slug}`}
          variant="outline"
          onClick={() => api.overlayHide()}
          className="h-14 px-5 border-slate-600 text-slate-200 hover:bg-[#181d2a]"
        >
          <EyeOff className="w-4 h-4 mr-2" /> Nascondi overlay
        </Button>
        {!compact && (
          <Link to="/content">
            <Button variant="ghost" className="h-14 px-5 text-slate-300 hover:text-amber-400" data-testid={`manage-${slug}`}>
              <Settings2 className="w-4 h-4 mr-2" /> Gestisci elementi
            </Button>
          </Link>
        )}
      </div>

      {result && (
        <div
          data-testid={`result-${slug}`}
          className="mt-6 w-full max-w-xl text-center bg-[#131722] border-2 rounded-xl p-6 animate-sw-pop"
          style={{ borderColor: accent.color }}
        >
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">{wheel.title}</div>
          <div className="font-display text-4xl md:text-5xl font-black text-amber-300 uppercase" style={{ textShadow: "0 0 22px rgba(245,158,11,0.5)" }}>
            {result.name}
          </div>
          {result.description ? <div className="text-slate-300 mt-2">{result.description}</div> : null}
        </div>
      )}
    </div>
  );
}
